import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { projectNav } from '@/components/px/nav'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { CaptureChannelsPanel } from '@/features/intakes/components/CaptureChannelsPanel'
import { IntakesPanel } from '@/features/intakes/components/IntakesPanel'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { openProject, selectProjectBlocked, selectProjectDetail } from '@/features/projects/slices/projectDetailSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * SURFACE 3.2 — DISCOVER · PAX IN THE WORKSHOP (`/project/:projectId/interviews`),
 * WIRED (PX-W7.1, TASK-A30F4433): the project's real intakes, each intake's
 * per-assignee sessions, a selected session's real transcript + analysis, and
 * the org-admin meeting-capture surface. The layout, panel set and visual
 * language are kept exactly as the prototype built them (mockup 3.2) — only
 * the data source changes. The two top-level panels each own their own
 * `features/intakes` slice load (the PX-W6.3 pattern); the transcript and
 * analysis panels are pure selectors over the state `IntakesPanel`'s
 * `loadSession` populates, so one selection never races two fetches.
 *
 * The chrome is built from the route param, i18n keys and the live
 * `selectProjectDetail` row ONLY — never a demo hero (memory
 * `px-hybrid-route-chrome-leaks-the-fixture`).
 *
 * PX-W7.1 fix cycle 1 (GRP-B3D9F416, finding 2): the page now dispatches
 * `openProject` on mount (`shouldFetch`-guarded — a no-op when the overview
 * already loaded the same project) and renders `ProjectBlockedState` AHEAD of
 * the panels when that read is blocked. Before this fix a non-member's 403 on
 * the project read was invisible here — `versions`, the first request either
 * panel actually issues, refuses with the SAME 403, but `IntakesPanel`'s
 * versions-empty branch rendered its "no discovery cycle yet" empty state
 * before ever reaching its own blocked check (AC-6), which is exactly the
 * empty-workspace-instead-of-a-refusal regression `ProjectBlockedState` exists
 * to prevent. The panels keep their OWN blocked routing for their OWN reads
 * (a caller who passes the project gate but is refused deeper in, e.g. a
 * specific intake) — this only covers the project-level gate.
 */
export default function InterviewsPage() {
  const { t } = useTranslation('projectWorkspace')
  const { projectId } = useParams()
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const project = useAppSelector(selectProjectDetail)
  const blocked = useAppSelector(selectProjectBlocked)

  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openProject({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  // PX-W6.6: the project rail follows the ROUTE and the LIVE row — never the
  // demo hero (memory `px-hybrid-route-chrome-leaks-the-fixture`).
  const rail = { id: projectId ?? '', client: project?.name ?? projectId ?? '' }

  return (
    <AppShell
      moduleLabel={t('commercial:chrome.project')}
      breadcrumb={<span>{t('intakes.pageTitle')}</span>}
      contextPill={(project?.office ?? t('contextPill')).toUpperCase()}
      nav={projectNav(rail)}
    >
      {blocked !== null ? (
        <ProjectBlockedState blocked={blocked} />
      ) : (
        <div className="w-full">
          <header className="max-w-3xl">
            <h1 className="font-serif text-[26px] font-semibold leading-8 text-foreground">
              {t('intakes.pageTitle')}
            </h1>
            <p className="mt-1.5 text-sm text-ink-soft">{t('intakes.pageLede')}</p>
          </header>

          <div className="mt-5 space-y-4">
            <IntakesPanel />
            <CaptureChannelsPanel />
          </div>

          <DensityNote>{t('intakes.densityNote')}</DensityNote>
        </div>
      )}
    </AppShell>
  )
}
